import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Beer, BookOpen, Users, Heart } from '../../lib/lucide-proxy';
import Button from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const HowItWorksPage = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: BookOpen,
      title: 'Conhecimento',
      description: 'Explore nossa enciclopédia e aprenda sobre estilos, IBUs, ABVs e harmonizações. Entenda o que você está bebendo.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Beer,
      title: 'Degustação',
      description: 'Encontre cervejarias artesanais locais autênticas. Nada de cerveja de milho. Aqui é malte, lúpulo e paixão.',
      color: 'bg-amber-100 text-amber-600'
    },
    {
      icon: Users,
      title: 'Comunidade',
      description: 'Avalie as cervejarias, compartilhe suas experiências e ajude outros confrades a encontrarem o gole perfeito.',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Heart,
      title: 'Apoio Local',
      description: 'Fortaleça a economia local comprando direto de quem produz. Frescor garantido no seu copo.',
      color: 'bg-red-100 text-red-600'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Experiência Cervejeira - Cerveja Delivery</title>
      </Helmet>

      <div className="bg-cream-300 min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-primary-100/50 rounded-l-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <span className="inline-block py-1 px-3 rounded-full bg-primary-100 text-primary-700 text-sm font-bold mb-4 tracking-wider uppercase">
                Mais que Delivery
              </span>
              <h1 className="font-display font-bold text-5xl md:text-6xl text-dark-900 mb-6">
                A Arte de Beber Bem
              </h1>
              <p className="text-xl text-dark-600 max-w-3xl mx-auto leading-relaxed">
                O Cerveja Delivery não é apenas sobre entregar garrafas. É sobre conectar você à cultura cervejeira, descobrir novos sabores e valorizar o mestre cervejeiro do seu bairro.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pillars */}
        <section className="py-16 bg-white rounded-t-[3rem]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <motion.div 
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-medium transition-all duration-300 border border-gray-100"
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${step.color}`}>
                    <step.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-dark-900 mb-3">{step.title}</h3>
                  <p className="text-dark-600 leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-20 bg-dark-900 rounded-3xl p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1535958636474-b021ee8876a3?auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center"></div>
              <div className="relative z-10">
                <h2 className="font-display font-bold text-3xl text-white mb-6">
                  Quer aprender mais sobre Cervejas?
                </h2>
                <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                  Visite nossa Enciclopédia Colaborativa. Descubra estilos, histórias e curiosidades.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="primary" onClick={() => navigate('/cervejas')}>
                    Acessar Enciclopédia
                  </Button>
                  <Button variant="outline" className="text-white border-white hover:bg-white hover:text-dark-900" onClick={() => navigate('/cervejarias')}>
                    Explorar Cervejarias
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HowItWorksPage;